/**
 * Cookie Backup Utility
 * Provides a fallback mechanism for localStorage by mirroring values to cookies.
 * Cookies act as a silent backup - localStorage remains the primary source.
 */

// Keys that should be backed up to cookies
const AUTH_KEYS = [
  'access_token',
  'access_token_vr',
  'expires_in',
  'DeviceId',
  'respo'
];

/**
 * Set a cookie with the given key and value
 * @param {string} key - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration in days (default: 365)
 */
function setCookie(key, value, days = 365) {
  if (value === null || value === undefined) return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value by key
 * @param {string} key - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
function getCookie(key) {
  const nameEQ = encodeURIComponent(key) + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Remove a cookie by key
 * @param {string} key - Cookie name
 */
function removeCookie(key) {
  document.cookie = `${encodeURIComponent(key)}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
}

/**
 * Backup a localStorage value to cookies
 * Call this after every localStorage.setItem for auth-related keys
 * @param {string} key - The localStorage key
 * @param {string} value - The value to backup
 */
function backupToookie(key, value) {
  if (AUTH_KEYS.includes(key)) {
    setCookie(key, value);
  }
}

/**
 * Remove a value from cookie backup
 * Call this after every localStorage.removeItem for auth-related keys
 * @param {string} key - The localStorage key
 */
function removeFromCookieBackup(key) {
  if (AUTH_KEYS.includes(key)) {
    removeCookie(key);
  }
}

/**
 * Rehydrate localStorage from cookies
 * Should be called once at app startup (before any auth checks)
 * Only restores values that are missing from localStorage
 */
function rehydrateFromCookies() {
  AUTH_KEYS.forEach((key) => {
    const localValue = localStorage.getItem(key);
    if (localValue === null || localValue === undefined) {
      const cookieValue = getCookie(key);
      if (cookieValue !== null) {
        localStorage.setItem(key, cookieValue);
        console.log(`[CookieBackup] Restored ${key} from cookie backup`);
      }
    }
  });
}

/**
 * Clear all auth-related cookies
 * Call this on logout
 */
function clearAllAuthCookies() {
  AUTH_KEYS.forEach((key) => {
    removeCookie(key);
  });
}

export {
  setCookie,
  getCookie,
  removeCookie,
  backupToookie,
  removeFromCookieBackup,
  rehydrateFromCookies,
  clearAllAuthCookies,
  AUTH_KEYS
};
